import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useParamReplace, useGetActiveProjectId } from '@/utils/hooks';
import { Trans } from '@lingui/react/macro';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { Redirect, Route, Switch, useParams, useRouteMatch } from 'react-router';
import { ProjectPermissionToggle } from '@/components/permissions';
import BuyerOptionPresetGroupEditDialog from './BuyerOptionPresetGroupEditDialog';
import { BuyerOptionPresetGroupPage } from './BuyerOptionPresetGroupPage';
import { SellerStyleCard } from '@/components/OptionConfigurator/StyleCard';
import { OptionPresetGroupType, useOptionPresetGroups } from '@/services/option-preset/option-preset-group';
import { Button, ButtonBar, NoItemsCard, Tabs } from '@ygtechnologies/rubus-ui';

interface BuyerOptionPresetsPageProps {
    projectId: number;
}

const BuyerOptionPresetsPage: React.FC<BuyerOptionPresetsPageProps> = ({ projectId }) => {
    const { path } = useRouteMatch();

    return (
        <Switch>
            <Route path={`${path}/group/:groupId`} component={BuyerOptionPresetGroupPage} />
            <Route path={`${path}/:styleType`} component={StylesListingPage} />
            <Redirect to={`${path}/Main`} />
        </Switch>
    );
};

const StylesListingPage: React.FC = () => {
    const { styleType } = useParams<{ styleType?: OptionPresetGroupType }>();
    const activeTab = styleType || OptionPresetGroupType.Main;
    const projectId = useGetActiveProjectId();
    const { data: presetGroups, refetch } = useOptionPresetGroups(projectId!);

    const replaceParams = useParamReplace<{ styleType?: string }>();
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const handlePresetUpdate = () => {
        refetch();
    };

    const filteredGroups = presetGroups?.filter((g) => {
        if (activeTab === OptionPresetGroupType.Main) {
            return g.type === OptionPresetGroupType.Main;
        }
        if (activeTab === OptionPresetGroupType.Room) {
            return g.type === OptionPresetGroupType.Room;
        }
        return false;
    });

    return (
        <>
            <ButtonBar sticky margins="none">
                <Tabs activeId={activeTab} variant="pill" size="sm">
                    <Tabs.Tab
                        id={OptionPresetGroupType.Main}
                        as={Link}
                        to={replaceParams({ styleType: OptionPresetGroupType.Main })}
                        label={<Trans>Main</Trans>}
                    />
                    <Tabs.Tab
                        id={OptionPresetGroupType.Room}
                        as={Link}
                        to={replaceParams({ styleType: OptionPresetGroupType.Room })}
                        label={<Trans>Room</Trans>}
                    />
                </Tabs>
                <ProjectPermissionToggle condition="isAdministrator">
                    <ButtonBar.Right>
                        <Button onClick={() => setCreateModalOpen(true)} icon={faPlus} variant="tertiary">
                            <Trans id="create_a_new_style_group">Create a new group</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ProjectPermissionToggle>
            </ButtonBar>
            <div style={{ gap: '20px', display: 'flex', flexDirection: 'column', marginTop: '20px' }}>
                {!presetGroups ? (
                    <LoadingIndicator />
                ) : filteredGroups && filteredGroups.length > 0 ? (
                    filteredGroups.map((g) => <SellerStyleCard key={g.id} group={g} />)
                ) : (
                    <NoItemsCard
                        title={<Trans>There are no groups!</Trans>}
                        description={<Trans>Want to create a group?</Trans>}
                        buttonProps={{
                            icon: faPlus,
                            onClick: () => setCreateModalOpen(true),
                            children: <Trans id="create_a_new_style_group">Create a new group</Trans>
                        }}
                    />
                )}
            </div>
            <BuyerOptionPresetGroupEditDialog
                presetType={activeTab as OptionPresetGroupType}
                onSave={handlePresetUpdate}
                onClose={() => setCreateModalOpen(false)}
                show={createModalOpen}
            />
        </>
    );
};

export default BuyerOptionPresetsPage;
